import clsx from "clsx";
import {
  ActionButton,
  AmenitiesSelection,
  BannerSeverity,
  FilterPill,
  GenericDropdown,
  getCurrencySymbol,
  HotelAvailabilityFilter,
  HotelPriceSelection,
  Icon,
  IconName,
  LoyaltyProgramsSelection,
  mapAmenitiesToTopAmenities,
  NotificationBanner,
  PolicyStatusSelection,
  StarRatingsSelection,
  StayTypeFilter,
  useDeviceTypes,
  usePrevious,
} from "halifax";
import { debounce, isEqual } from "lodash-es";
import React, { useContext, useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router";
import "./styles.scss";

import { isCorpTenant, useShowPolicyBanner } from "@capone/common";
import {
  APPLIED_HOTEL_FILTER,
  AppliedHotelFilterProperties,
  CLEAR_ALL_FILTERS,
} from "redmond";
import { Skeleton } from "@material-ui/lab";
import { Box, Typography } from "@material-ui/core";
import { StayType } from "redmond/hotels-module/interfaces";
import config from "../../../../../../../cap1-application/b2b-base/src/utils/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../../../App";
import {
  addTrackingProperties,
  AVAILABLE,
  CONTROL,
  CORP_LOYALTY_FILTER_EXPERIMENT,
  CUSTOMER_PROFILE_EXPERIMENT,
  CUSTOMER_PROFILE_VARIANTS,
  FREE_BREAKFAST_CANCEL,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  SHOW_MEALPLAN,
  useExperiments,
} from "../../../../context/experiments";
import { initialFilterState, initialState } from "../../reducer/index";
import {
  HotelAvailabilityCallState,
  HotelAvailabilitySortOption,
} from "../../reducer/state";
import { sortOptions } from "../MobileLocationSelection/component";
import { StayTypeFilterPopover } from "../StayTypeFilterPopover";
import {
  shouldShowLifestyleStaysFilter,
  shouldShowPremiumStaysFilter,
  getStayTypeFilterConfig,
  getIsVacationRentalOnlyStayTypeSelected,
} from "../utilities";
import { AvailabilityFilterConnectorProps } from "./container";
import * as textConstants from "./textConstants";

import { useFilterLabelValues } from "./useFilterLabelValues";
import { VacationRentalAvailabilityFilter } from "../VacationRentalAvailabilityFilter";
import { VacationRentalAmenitiesSelection } from "../VacationRentalAvailabilityFilter/components/VacationRentalAmenitiesSelection";
import { VacationRentalRoomsAndBedsSelection } from "../VacationRentalAvailabilityFilter/components/VacationRentalRoomsAndBedsSelection";

export interface IAvailabilityFilterProps
  extends AvailabilityFilterConnectorProps,
    RouteComponentProps {}

export const AvailabilityFilter = (props: IAvailabilityFilterProps) => {
  const {
    locationLabel,
    amenities,
    bannerMessage,
    setAmenitiesFilter,
    starRatings,
    setStarRatingsFilter,
    maxPrice,
    setMaxPriceFilter,
    minMaxPriceRange,
    currency,
    hotelName,
    stayTypes,
    mealPlanTypes,
    setHotelNameFilter,
    hasChangedMaxPriceFilter,
    hasChangedAmenitiesFilter,
    hasChangedStarRatingsFilter,
    hasChangedHotelAvailabilityStayTypeFilter,
    setStayTypeFilter,
    setMealPlanTypeFilter,
    sortOption,
    setHotelAvailabilitySortOption,
    hasFreeCancelFilter,
    setFreeCancelFilter,
    hasHotelsOnSaleFilter,
    setHotelsOnSaleFilter,
    isInPolicy,
    setPolicyFilter,
    loyaltyPrograms,
    setLoyaltyProgramsFilter,
    userHotelPreferencesCallState,
    hasUserSetHotelPreferences,
    shouldApplyUserHotelPreferences,
    setApplyUserHotelPreferences,
    userHotelPreferences,
    appliedFilterCount,
    premiumStaysOnly,
    setPremiumStaysOnly,
    premiumStaysLodgingCount,
    lifestyleStaysOnly,
    setLifestyleStaysOnly,
    lifestyleStaysLodgingCount,
    hasChangedPolicyFilter,
    hotelAvailabilityCallState,
    availabilityResultsHasHomesAndHotels,
    vacationRentalAmenities,
    setVacationRentalAmenitiesFilter,
    hasChangedVacationRentalAmenitiesFilter,
    vacationRentalRoomsAndBeds,
    setVacationRentalRoomsAndBedsFilter,
    hasChangedVacationRentalRoomsAndBedsFilter,
    hasChangedLoyaltyFilter,
    selectedAccountReferenceId,
    rewardsAccounts,
  } = props;
  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [openVRFiltersModal, setOpenVRFiltersModal] =
    React.useState<boolean>(false);
  const stayTypeDropdownButtonRef = React.useRef<HTMLButtonElement | null>(
    null
  );
  const [localMaxPriceFilter, setLocalMaxPriceFilter] =
    React.useState<number>(maxPrice);

  const selectedAccount = useMemo(
    () =>
      rewardsAccounts.find(
        (r) => r.accountReferenceId === selectedAccountReferenceId
      ) ?? rewardsAccounts[0],
    [selectedAccountReferenceId, rewardsAccounts]
  );

  const expState = useExperiments();

  const isFreeBreakfastCancelExperiment =
    getExperimentVariant(expState.experiments, FREE_BREAKFAST_CANCEL) ===
    AVAILABLE;

  const isShowMealPlanExperiment =
    getExperimentVariant(expState.experiments, SHOW_MEALPLAN) === AVAILABLE;

  const { policies } = useContext(ClientContext);
  const showPolicyFilter = useShowPolicyBanner(policies);

  const {
    dedicatedStayTypeFilterEnabled,
    showVacationRentalSpecificFilters,
    showStayTypeFilterWithinAllFilters,
  } = getStayTypeFilterConfig({
    experiments: expState.experiments,
    availabilityResultsHasHomesAndHotels,
    stayTypes,
  });

  const isVacationRentalsOnlySelected =
    getIsVacationRentalOnlyStayTypeSelected(stayTypes);

  const previousStayTypes = usePrevious(stayTypes);

  const [showFilterLoadingState, setShowFilterLoadingState] =
    React.useState(false);

  useEffect(() => {
    const isPreviousStayTypeVacationRentalOnly =
      getIsVacationRentalOnlyStayTypeSelected(previousStayTypes);
    const isCurrentStayTypeVacationRentalOnly =
      getIsVacationRentalOnlyStayTypeSelected(stayTypes);
    if (
      isPreviousStayTypeVacationRentalOnly !==
      isCurrentStayTypeVacationRentalOnly
    ) {
      setShowFilterLoadingState(true);
      setTimeout(() => {
        setShowFilterLoadingState(false);
      }, 1000);
    }
  }, [dedicatedStayTypeFilterEnabled, stayTypes]);

  const isCustomerProfileExperiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      CUSTOMER_PROFILE_EXPERIMENT,
      CUSTOMER_PROFILE_VARIANTS
    ) !== CONTROL;

  const stayTypeToAbbreviatedTitle = {
    Hotels: "Hotels",
    Homes: "Vacation Rentals",
  };

  const showPremiumStaysFilter = React.useMemo(
    () =>
      premiumStaysOnly ||
      shouldShowPremiumStaysFilter(
        premiumStaysLodgingCount,
        expState.experiments,
        selectedAccount
      ),
    [
      premiumStaysOnly,
      premiumStaysLodgingCount,
      expState.experiments,
      selectedAccount,
    ]
  );

  const showLifestyleStaysFilter = React.useMemo(
    () =>
      lifestyleStaysOnly ||
      shouldShowLifestyleStaysFilter(
        lifestyleStaysLodgingCount,
        expState.experiments,
        selectedAccount
      ),
    [
      lifestyleStaysOnly,
      lifestyleStaysLodgingCount,
      expState.experiments,
      selectedAccount,
    ]
  );

  React.useEffect(() => {
    setLocalMaxPriceFilter(maxPrice);
  }, [maxPrice]);

  const isCorporateTenant = isCorpTenant(config.TENANT);
  const showLoyaltyFilter =
    getExperimentVariant(
      expState.experiments,
      CORP_LOYALTY_FILTER_EXPERIMENT
    ) === AVAILABLE;

  const readyToReset =
    !isEqual(amenities, initialFilterState.amenities) ||
    !isEqual(starRatings, initialFilterState.starRatings) ||
    !isEqual(isInPolicy, initialFilterState.isInPolicy) ||
    !isEqual(loyaltyPrograms, initialFilterState.loyaltyPrograms) ||
    premiumStaysOnly !== initialFilterState.showPremiumStaysOnly ||
    lifestyleStaysOnly !== initialFilterState.showLifestyleStaysOnly ||
    maxPrice !== initialFilterState.maxPrice ||
    !isEqual(hotelName, initialFilterState.hotelName) ||
    (!dedicatedStayTypeFilterEnabled &&
      !isEqual(stayTypes, initialFilterState.stayTypes)) ||
    !isEqual(mealPlanTypes, initialFilterState.mealPlanTypes) ||
    (isVacationRentalsOnlySelected &&
      !isEqual(vacationRentalAmenities, initialFilterState.vrAmenities)) ||
    (isVacationRentalsOnlySelected &&
      !isEqual(vacationRentalRoomsAndBeds, initialFilterState.vrRoomsAndBeds));

  const {
    ratingLabel,
    amenitiesLabel,
    vacationRentalAmenitiesLabel,
    vacationRentalRoomsAndBedsLabel,
  } = useFilterLabelValues({
    starRatings,
    amenities,
    vacationRentalAmenities,
    vacationRentalRoomsAndBeds,
  });

  const setMaxPriceFilterHandler = (maxPrice: number) => {
    setMaxPriceFilter(maxPrice);
    const properties: AppliedHotelFilterProperties = {
      filter_type: "price",
    };
    trackEvent({
      eventName: APPLIED_HOTEL_FILTER,
      properties,
    });
  };

  const setMaxPriceFilterDebounced = React.useMemo(
    () => debounce(setMaxPriceFilterHandler, 150),
    []
  );

  React.useEffect(() => {
    if (
      ![
        HotelAvailabilityCallState.InitialSearchCallInProcess,
        HotelAvailabilityCallState.NotCalled,
      ].includes(hotelAvailabilityCallState) &&
      shouldApplyUserHotelPreferences &&
      userHotelPreferences &&
      setApplyUserHotelPreferences &&
      (!isEqual(
        userHotelPreferences.amenities,
        mapAmenitiesToTopAmenities(amenities).map(
          (topAmenity) => topAmenity.value
        )
      ) ||
        !isEqual(userHotelPreferences.starRatings, starRatings) ||
        userHotelPreferences.freeCancellationOnly !== hasFreeCancelFilter)
    ) {
      setApplyUserHotelPreferences(false);
    }
  }, [
    starRatings,
    amenities,
    hasFreeCancelFilter,
    shouldApplyUserHotelPreferences,
    userHotelPreferences,
    userHotelPreferencesCallState,
    hotelAvailabilityCallState,
  ]);

  const renderDropdownButtons = () => (
    <>
      <GenericDropdown
        buttonClassName={clsx(
          "hotel-availability-dropdown",
          "star-ratings",
          "b2b-shop-filter",
          "enhanced-hotel-filters",
          {
            "has-value": hasChangedStarRatingsFilter,
            "filter-loading": showFilterLoadingState,
          }
        )}
        popoverClassName={clsx(
          "hotel-availability-star-ratings-dropdown-popover",
          "b2b",
          "enhanced-hotel-filters"
        )}
        ariaLabel={`${textConstants.RATING_TEXT} filter`}
        disabled={showFilterLoadingState}
        dropdownLabel={
          showFilterLoadingState ? (
            <Skeleton variant="text" width="62px" />
          ) : (
            <>
              <strong>
                {textConstants.RATING_TEXT}
                {hasChangedStarRatingsFilter ? ": " : ""}
              </strong>
              {hasChangedStarRatingsFilter ? ratingLabel : ""}
            </>
          )
        }
        dropdownIcon={
          showFilterLoadingState ? (
            <></>
          ) : hasChangedStarRatingsFilter ? (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setStarRatingsFilter([]);
              }}
            >
              <Icon name={IconName.XCircle} />
            </div>
          ) : undefined
        }
        dropdownContent={
          <StarRatingsSelection
            className="desktop-hotel-availability-star-ratings"
            starRatings={starRatings}
            setStarRatings={(starRatings) => setStarRatingsFilter(starRatings)}
          />
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      />
      {!showVacationRentalSpecificFilters && (
        <GenericDropdown
          buttonClassName={clsx(
            "hotel-availability-dropdown",
            "amenities",
            "b2b-shop-filter",
            "enhanced-hotel-filters",
            {
              "has-value": hasChangedAmenitiesFilter,
              "filter-loading": showFilterLoadingState,
            }
          )}
          popoverClassName={clsx(
            "hotel-availability-amenities-dropdown-popover",
            "b2b",
            "enhanced-hotel-filters"
          )}
          ariaLabel={`${textConstants.AMENITIES_FILTER_LABEL} filter`}
          dropdownLabel={
            showFilterLoadingState ? (
              <Skeleton variant="text" width="84px" />
            ) : (
              <>
                <strong>
                  {textConstants.AMENITIES_FILTER_LABEL}
                  {hasChangedAmenitiesFilter ? ": " : ""}
                </strong>
                {hasChangedAmenitiesFilter ? amenitiesLabel : ""}
              </>
            )
          }
          dropdownIcon={
            showFilterLoadingState ? (
              <></>
            ) : hasChangedAmenitiesFilter ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setAmenitiesFilter([]);
                }}
              >
                <Icon name={IconName.XCircle} />
              </div>
            ) : undefined
          }
          dropdownContent={
            <AmenitiesSelection
              amenities={amenities}
              setAmenities={setAmenitiesFilter}
              useTopAmenities
            />
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        />
      )}
      {showVacationRentalSpecificFilters && (
        <GenericDropdown
          buttonClassName={clsx(
            "hotel-availability-dropdown",
            "amenities",
            "b2b-shop-filter",
            "enhanced-hotel-filters",
            {
              "has-value": hasChangedVacationRentalAmenitiesFilter,
              "filter-loading": showFilterLoadingState,
            }
          )}
          popoverClassName={clsx(
            "hotel-availability-vr-amenities-dropdown-popover",
            "b2b",
            "enhanced-hotel-filters"
          )}
          disabled={showFilterLoadingState}
          ariaLabel={`${textConstants.AMENITIES_FILTER_LABEL} filter`}
          dropdownLabel={
            showFilterLoadingState ? (
              <Skeleton variant="text" width="62px" />
            ) : (
              <>
                <strong>
                  {textConstants.AMENITIES_FILTER_LABEL}
                  {hasChangedVacationRentalAmenitiesFilter ? ": " : ""}
                </strong>
                {hasChangedVacationRentalAmenitiesFilter
                  ? vacationRentalAmenitiesLabel
                  : ""}
              </>
            )
          }
          dropdownIcon={
            showFilterLoadingState ? (
              <></>
            ) : hasChangedVacationRentalAmenitiesFilter ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setVacationRentalAmenitiesFilter([]);
                }}
              >
                <Icon name={IconName.XCircle} />
              </div>
            ) : undefined
          }
          dropdownContent={
            <VacationRentalAmenitiesSelection
              isMobile={matchesMobile}
              variant="filter-dropdown-popover"
              vrAmenities={vacationRentalAmenities}
              setVrAmenities={setVacationRentalAmenitiesFilter}
            />
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        />
      )}
      {showVacationRentalSpecificFilters && (
        <GenericDropdown
          buttonClassName={clsx(
            "hotel-availability-dropdown",
            "b2b-shop-filter",
            "enhanced-hotel-filters",
            {
              "has-value": hasChangedVacationRentalRoomsAndBedsFilter,
              "filter-loading": showFilterLoadingState,
            }
          )}
          popoverClassName={clsx(
            "hotel-availability-vr-rooms-and-beds-dropdown-popover",
            "b2b",
            "enhanced-hotel-filters"
          )}
          ariaLabel={`${textConstants.ROOMS_AND_BEDS_FILTER_LABEL} filter`}
          disabled={showFilterLoadingState}
          dropdownLabel={
            showFilterLoadingState ? (
              <Skeleton variant="text" width="125px" />
            ) : (
              <>
                <strong>
                  {textConstants.ROOMS_AND_BEDS_FILTER_LABEL}
                  {hasChangedVacationRentalRoomsAndBedsFilter ? ": " : ""}
                </strong>
                {hasChangedVacationRentalRoomsAndBedsFilter
                  ? vacationRentalRoomsAndBedsLabel
                  : ""}
              </>
            )
          }
          dropdownIcon={
            showFilterLoadingState ? (
              <></>
            ) : hasChangedVacationRentalRoomsAndBedsFilter ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setVacationRentalRoomsAndBedsFilter(undefined);
                }}
              >
                <Icon name={IconName.XCircle} />
              </div>
            ) : undefined
          }
          dropdownContent={
            <VacationRentalRoomsAndBedsSelection
              isMobile={matchesMobile}
              vrRoomsAndBeds={vacationRentalRoomsAndBeds}
              setVrRoomsAndBeds={setVacationRentalRoomsAndBedsFilter}
              variant="filter-dropdown-popover"
            />
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        />
      )}
      {isFreeBreakfastCancelExperiment ? (
        <FilterPill
          onClick={(isSelected: boolean) => setFreeCancelFilter(!isSelected)}
          selected={hasFreeCancelFilter}
          className="free-cancel hotel-availability-filter-pill"
        >
          {showFilterLoadingState ? (
            <Skeleton variant="text" width="130px" />
          ) : (
            <b>{textConstants.FREE_CANCELLATION}</b>
          )}
        </FilterPill>
      ) : (
        <GenericDropdown
          buttonClassName={clsx(
            "hotel-availability-dropdown",
            "price",
            "b2b-shop-filter",
            "enhanced-hotel-filters",
            {
              "has-value": hasChangedMaxPriceFilter,
              "filter-loading": showFilterLoadingState,
            }
          )}
          popoverClassName={clsx(
            "hotel-availability-price-dropdown-popover",
            "b2b",
            "enhanced-hotel-filters"
          )}
          ariaLabel={`${textConstants.PRICE_PER_NIGHT_TEXT} filter`}
          disabled={showFilterLoadingState}
          dropdownLabel={
            showFilterLoadingState ? (
              <Skeleton variant="text" width="62px" />
            ) : (
              <>
                <strong>
                  {textConstants.PRICE_FILTER_LABEL}
                  {hasChangedMaxPriceFilter ? ":" : ""}
                </strong>
                {hasChangedMaxPriceFilter
                  ? ` ${getCurrencySymbol(currency)}${maxPrice}`
                  : ""}
              </>
            )
          }
          dropdownIcon={
            showFilterLoadingState ? (
              <></>
            ) : hasChangedMaxPriceFilter ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setMaxPriceFilter(initialFilterState.maxPrice);
                }}
              >
                <Icon name={IconName.XCircle} />
              </div>
            ) : undefined
          }
          dropdownContent={
            <HotelPriceSelection
              className="desktop-hotel-availability-price enhanced-hotel-filters"
              title={textConstants.PRICE_FILTER_TITLE}
              subtitle={textConstants.PRICE_FILTER_SUBTITLE}
              setHotelMaxPrice={setMaxPriceFilter}
              hotelPriceRange={minMaxPriceRange || { min: 0, max: 0 }}
              hotelMaxPrice={Math.ceil(minMaxPriceRange?.max ?? 0)}
              currencySymbol={getCurrencySymbol(currency)}
              type="max"
              alwaysShowTooltip
              showMinLabel
              showClearButton
              reset={() => setMaxPriceFilter(initialFilterState.maxPrice)}
            />
          }
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        />
      )}
      {showStayTypeFilterWithinAllFilters && (
        <>
          <GenericDropdown
            buttonClassName={clsx(
              "hotel-availability-dropdown",
              "stay-type",
              "b2b-shop-filter",
              "enhanced-hotel-filters",
              {
                "has-value": hasChangedHotelAvailabilityStayTypeFilter,
                "filter-loading": showFilterLoadingState,
              }
            )}
            popoverClassName={clsx(
              "hotel-availability-stay-type-dropdown-popover",
              "b2b",
              "enhanced-hotel-filters"
            )}
            ariaLabel={`${textConstants.PROPERTY_TYPE_TEXT} filter`}
            disabled={showFilterLoadingState}
            dropdownLabel={
              showFilterLoadingState ? (
                <Skeleton variant="text" width="107px" />
              ) : (
                <>
                  <strong>
                    {textConstants.PROPERTY_TYPE_TEXT}
                    {hasChangedHotelAvailabilityStayTypeFilter ? ": " : ""}
                  </strong>
                  {hasChangedHotelAvailabilityStayTypeFilter
                    ? stayTypes
                        .map((st) => stayTypeToAbbreviatedTitle[st])
                        .join(", ")
                    : ""}
                </>
              )
            }
            dropdownIcon={
              showFilterLoadingState ? (
                <></>
              ) : hasChangedHotelAvailabilityStayTypeFilter ? (
                <div
                  role="image"
                  aria-label={`${textConstants.PROPERTY_TYPE_TEXT} filter clear button`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStayTypeFilter([]);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      setStayTypeFilter([]);
                    }
                  }}
                  tabIndex={0}
                >
                  <Icon name={IconName.XCircle} />
                </div>
              ) : undefined
            }
            dropdownContent={
              <StayTypeFilter
                className="desktop-hotel-availability-stay-type"
                stayTypes={stayTypes}
                setStayTypes={(stayTypes) => setStayTypeFilter(stayTypes)}
                showStayTypeFilter={availabilityResultsHasHomesAndHotels}
              />
            }
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            ref={stayTypeDropdownButtonRef}
          />
          {stayTypeDropdownButtonRef.current && (
            <StayTypeFilterPopover anchorRef={stayTypeDropdownButtonRef} />
          )}
        </>
      )}
      {hotelName && (
        <Box className={clsx("hotel-filter-pill", "hotel-name")}>
          <Box className="filter-label-wrapper">
            <span className="filter-label-text">
              {textConstants.HOTEL_NAME_FILTER_LABEL(hotelName)}
            </span>
          </Box>
          <button onClick={() => setHotelNameFilter("")}>
            <Icon name={IconName.XCircle} />
          </button>
        </Box>
      )}
      {hasHotelsOnSaleFilter && (
        <Box className={clsx("hotel-filter-pill", "hotels-on-sale")}>
          <Box className="filter-label-wrapper">
            <span className="filter-label-text">
              {textConstants.HOTELS_ON_SALE}
            </span>
          </Box>
          <button onClick={() => setHotelsOnSaleFilter(false)}>
            <Icon name={IconName.XCircle} />
          </button>
        </Box>
      )}
      {isCorporateTenant && (
        <>
          {showLoyaltyFilter && (
            <GenericDropdown
              buttonClassName={clsx(
                "hotel-availability-dropdown",
                "b2b-shop-filter",
                "enhanced-hotel-filters",
                {
                  "has-value": hasChangedLoyaltyFilter,
                }
              )}
              popoverClassName={clsx(
                "hotel-availability-loyalty-programs-dropdown-popover",
                "b2b"
              )}
              ariaLabel="Hotel loyalty program filter"
              dropdownLabel={
                <strong>{textConstants.HOTELS_LOYALTY_PROGRAMS}</strong>
              }
              dropdownContent={
                <LoyaltyProgramsSelection
                  className="desktop-hotel-availability-loyalty-programs"
                  loyaltyPrograms={loyaltyPrograms}
                  setLoyaltyPrograms={(loyaltyPrograms: string[]) =>
                    setLoyaltyProgramsFilter(loyaltyPrograms)
                  }
                />
              }
              dropdownIcon={
                hasChangedLoyaltyFilter ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setLoyaltyProgramsFilter([]);
                    }}
                  >
                    <Icon name={IconName.XCircle} />
                  </div>
                ) : undefined
              }
            />
          )}
          {showPolicyFilter && (
            <GenericDropdown
              buttonClassName={clsx(
                "hotel-availability-dropdown",
                "b2b-shop-filter",
                "enhanced-hotel-filters",
                {
                  "has-value": hasChangedPolicyFilter,
                }
              )}
              popoverClassName={clsx(
                "hotel-availability-policy-dropdown-popover",
                "b2b"
              )}
              ariaLabel="Policy status filter"
              dropdownLabel={
                <strong>{textConstants.HOTELS_POLICY_STATUS}</strong>
              }
              dropdownContent={
                <PolicyStatusSelection
                  isInPolicy={isInPolicy}
                  setIsInPolicy={setPolicyFilter}
                />
              }
              dropdownIcon={
                hasChangedPolicyFilter ? (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setPolicyFilter(false);
                    }}
                  >
                    <Icon name={IconName.XCircle} />
                  </div>
                ) : undefined
              }
            />
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <ActionButton
        className={clsx(
          "hotel-availability-filter-action-button",
          "enhanced-hotel-filters",
          {
            mobile: matchesMobile,
            "filter-loading": showFilterLoadingState,
          }
        )}
        defaultStyle="h4r-secondary"
        message={
          showFilterLoadingState ? (
            <Skeleton variant="text" width="112px" />
          ) : (
            textConstants.SORT_DROPDOWN_LABEL_EXPERIMENT(appliedFilterCount)
          )
        }
        disabled={showFilterLoadingState}
        onClick={() => {
          if (showVacationRentalSpecificFilters) {
            setOpenVRFiltersModal(true);
          } else {
            setOpenModal(true);
          }
        }}
        icon={
          showFilterLoadingState ? undefined : <Icon name={IconName.Settings} />
        }
      />
      {matchesDesktop && showPremiumStaysFilter && (
        <FilterPill
          onClick={(isSelected: boolean) => {
            setPremiumStaysOnly(!isSelected, {
              location: "list",
              searched_market: locationLabel,
            });
          }}
          selected={premiumStaysOnly}
          className="premium-stays-only hotel-availability-filter-pill"
        >
          {showFilterLoadingState ? (
            <Skeleton variant="text" width="130px" />
          ) : (
            <b>{textConstants.PREMIUM_STAYS_ONLY_LABEL}</b>
          )}
        </FilterPill>
      )}
      {matchesDesktop &&
        showLifestyleStaysFilter &&
        !showPremiumStaysFilter &&
        !showFilterLoadingState && (
          <FilterPill
            onClick={(isSelected: boolean) => {
              setLifestyleStaysOnly(!isSelected, {
                location: "list",
                searched_market: locationLabel,
              });
            }}
            selected={lifestyleStaysOnly}
            className="premium-stays-only hotel-availability-filter-pill"
          >
            {showFilterLoadingState ? (
              <Skeleton variant="text" width="130px" />
            ) : (
              <b>{textConstants.PREMIUM_STAYS_ONLY_LABEL}</b>
            )}
          </FilterPill>
        )}
      {matchesDesktop && renderDropdownButtons()}
      {bannerMessage != null && (
        <NotificationBanner
          className="hotel-availability-filter-banner standard-style"
          content={<Typography>{bannerMessage}</Typography>}
          severity={BannerSeverity.NOTICE}
        />
      )}
      <HotelAvailabilityFilter
        openModal={openModal}
        onClose={() => setOpenModal(false)}
        isInPolicy={isInPolicy}
        setIsInPolicy={setPolicyFilter}
        isMobile={matchesMobile}
        amenities={amenities}
        setAmenities={setAmenitiesFilter}
        starRatings={starRatings}
        setStarRatings={setStarRatingsFilter}
        premiumStays={
          showPremiumStaysFilter
            ? {
                value: premiumStaysOnly,
                set: (enable) =>
                  setPremiumStaysOnly(enable, {
                    location: "all_filters_modal",
                    searched_market: locationLabel,
                  }),
                premiumHotelsCount: premiumStaysLodgingCount.filtered,
              }
            : undefined
        }
        lifestyleStays={
          showLifestyleStaysFilter && !showPremiumStaysFilter
            ? {
                value: lifestyleStaysOnly,
                set: (enable) =>
                  setLifestyleStaysOnly(enable, {
                    location: "all_filters_modal",
                    searched_market: locationLabel,
                  }),
                lifestyleHotelsCount: lifestyleStaysLodgingCount.filtered,
              }
            : undefined
        }
        hotelPriceRange={minMaxPriceRange || { min: 0, max: 0 }}
        setHotelMaxPrice={setMaxPriceFilterDebounced}
        hotelMaxPrice={localMaxPriceFilter}
        currencySymbol={getCurrencySymbol(currency)}
        hotelName={hotelName}
        setHotelName={setHotelNameFilter}
        readyToReset={readyToReset}
        sortOptions={sortOptions.map((value) => ({
          value,
          label: textConstants.sortOptionLabel[value],
        }))}
        selectedSortOption={sortOption}
        setSelectedSortOption={(value) =>
          setHotelAvailabilitySortOption(value as HotelAvailabilitySortOption)
        }
        isFreeCancelExperiment={isFreeBreakfastCancelExperiment}
        showLoyaltyFilter={showLoyaltyFilter}
        setFreeCancelFilter={setFreeCancelFilter}
        hasFreeCancelFilter={hasFreeCancelFilter}
        setHotelsOnSaleFilter={setHotelsOnSaleFilter}
        hasHotelsOnSaleFilter={hasHotelsOnSaleFilter}
        defaultSortOption={initialState.sortOption}
        titles={textConstants.FILTER_MODAL_TITLES}
        loyaltyPrograms={loyaltyPrograms}
        setLoyaltyPrograms={setLoyaltyProgramsFilter}
        tenant={config.TENANT}
        showHotelPreferencesBanner={isCustomerProfileExperiment}
        userHotelPreferencesCallState={userHotelPreferencesCallState}
        userHasSetHotelPreferences={hasUserSetHotelPreferences}
        shouldApplyUserHotelPreferences={shouldApplyUserHotelPreferences}
        setShouldApplyUserHotelPreferences={setApplyUserHotelPreferences}
        userHotelPreferences={userHotelPreferences}
        onReset={() => {
          trackEvent({
            eventName: CLEAR_ALL_FILTERS,
            properties: addTrackingProperties(expState.trackingProperties),
          });
        }}
        showStayTypeFilter={showStayTypeFilterWithinAllFilters}
        stayTypes={stayTypes}
        setStayTypes={setStayTypeFilter}
        showPolicyFilter={showPolicyFilter}
        showMealPlanTypeFilter={isShowMealPlanExperiment}
        mealPlanTypes={mealPlanTypes}
        setMealPlanTypes={setMealPlanTypeFilter}
        showHotelsOnSaleFilter={!stayTypes.includes(StayType.Hotels)}
      />
      <VacationRentalAvailabilityFilter
        open={openVRFiltersModal}
        onClose={() => setOpenVRFiltersModal(false)}
        isMobile={matchesMobile}
        readyToReset={readyToReset}
        starRatings={starRatings}
        setStarRatings={setStarRatingsFilter}
        currencySymbol={getCurrencySymbol(currency)}
        maxPrice={localMaxPriceFilter}
        minMaxPriceRange={minMaxPriceRange}
        setMaxPriceFilter={setMaxPriceFilterDebounced}
        hasFreeCancelFilter={hasFreeCancelFilter}
        setFreeCancelFilter={setFreeCancelFilter}
        vrAmenities={vacationRentalAmenities}
        setVrAmenitiesFilter={setVacationRentalAmenitiesFilter}
        vrRoomsAndBeds={vacationRentalRoomsAndBeds}
        setVrRoomsAndBedsFilter={setVacationRentalRoomsAndBedsFilter}
        sortOptions={sortOptions.map((value) => ({
          value,
          label: textConstants.sortOptionLabel[value],
        }))}
        selectedSortOption={sortOption}
        setSelectedSortOption={(value) =>
          setHotelAvailabilitySortOption(value as HotelAvailabilitySortOption)
        }
        defaultSortOption={initialState.sortOption}
        premiumStays={
          showPremiumStaysFilter
            ? {
                value: premiumStaysOnly,
                set: (enable) =>
                  setPremiumStaysOnly(enable, {
                    location: "all_filters_modal",
                    searched_market: locationLabel,
                  }),
                premiumHotelsCount: premiumStaysLodgingCount.filtered,
              }
            : undefined
        }
        lifestyleStays={
          showLifestyleStaysFilter && !showPremiumStaysFilter
            ? {
                value: lifestyleStaysOnly,
                set: (enable) =>
                  setLifestyleStaysOnly(enable, {
                    location: "all_filters_modal",
                    searched_market: locationLabel,
                  }),
                lifestyleHotelsCount: lifestyleStaysLodgingCount.filtered,
              }
            : undefined
        }
        setHotelsOnSaleFilter={setHotelsOnSaleFilter}
        hasHotelsOnSaleFilter={hasHotelsOnSaleFilter}
      />
    </>
  );
};
